import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Search, Edit3, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatDate, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "../components/ui/card";

export default function AdminUsersPage() {
    const navigate = useNavigate();
    const [users, setUsers] = useState(null);
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(0);
    const [total, setTotal] = useState(0);
    const [error, setError] = useState("");
    const sequence = useRef(0);
    const [refreshing, setRefreshing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState("add"); // 'add' | 'edit'
    const [form, setForm] = useState({ id: null, email: "", fullName: "", role: "mitra", password: "", shopName: "" });

    const loadUsers = useCallback(async () => {
        const request = ++sequence.current;
        setError(""); setUsers(null);
        try {
            const { data } = await api.get("/admin/users", { params: { skip: page * 20, take: 20, ...(search.trim() && { search: search.trim() }) } });
            if (request !== sequence.current) return;
            setUsers(data.items ?? []); setTotal(data.total ?? 0);
        } catch (err) {
            if (request !== sequence.current) return;
            setError(formatApiError(err));
            setUsers([]);
        }
    }, [page, search]);
    useEffect(() => { const timer = setTimeout(loadUsers, 200); return () => { clearTimeout(timer); sequence.current++; }; }, [loadUsers]);
    const filteredUsers = users || [];

    const refresh = async () => {
        setRefreshing(true);
        await loadUsers();
        setRefreshing(false);
    };

    const toggleStatus = async (user) => {
        if (!window.confirm(`${user.status === "active" ? "Nonaktifkan" : "Aktifkan"} akun ${user.email}?`)) return;
        setSaving(true);
        try {
            if (user.status === "active") {
                await api.patch(`/admin/users/${user.id}/suspend`);
            } else {
                await api.patch(`/admin/users/${user.id}/activate`);
            }
            toast.success("Status user berhasil diperbarui.");
            await loadUsers();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const deleteUser = async (user) => {
        if (!window.confirm(`Hapus user ${user.email}? Aksi ini tidak dapat dibatalkan.`)) return;
        setSaving(true);
        try {
            await api.delete(`/admin/users/${user.id}`);
            toast.success("User berhasil dihapus.");
            await loadUsers();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const editUser = async (user) => {
        setModalMode("edit");
        setForm({ id: user.id, email: user.email || "", fullName: user.fullName || user.owner_name || "", role: user.role || "mitra", password: "", shopName: "" });
        setModalOpen(true);
    };

    const openAddUser = () => {
        setModalMode("add");
        setForm({ id: null, email: "", fullName: "", role: "mitra", password: "", shopName: "" });
        setModalOpen(true);
    };

    const saveUser = async () => {
        if (form.fullName.trim().length < 2 || form.fullName.length > 120) { toast.error("Nama harus terdiri dari 2–120 karakter."); return; }
        if (modalMode === "add" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) { toast.error("Masukkan email yang valid."); return; }
        if ((modalMode === "add" || form.password) && (form.password.length < 8 || form.password.length > 128 || !/[A-Za-z]/.test(form.password) || !/\d/.test(form.password))) { toast.error("Password harus 8–128 karakter dengan huruf dan angka."); return; }
        if (modalMode === "add" && form.role === "mitra" && !form.shopName.trim()) { toast.error("Isi nama warung untuk akun mitra."); return; }
        setSaving(true);
        try {
            if (modalMode === "add") {
                const payload = { email: form.email, fullName: form.fullName, role: form.role, password: form.password };
                if (form.role === "mitra") payload.shopName = form.shopName.trim();
                await api.post(`/admin/users`, payload);
                toast.success("User berhasil ditambahkan.");
            } else {
                const payload = { fullName: form.fullName, role: form.role };
                if (form.role === "mitra" && form.shopName.trim()) payload.shopName = form.shopName.trim();
                if (form.password) payload.password = form.password;
                await api.patch(`/admin/users/${form.id}`, payload);
                toast.success("User berhasil diperbarui.");
            }
            setModalOpen(false);
            await loadUsers();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-6">
            <div className="flex flex-col gap-4">
                <div className="flex items-center justify-start gap-4">
                    <Button aria-label="Kembali ke dashboard admin" variant="ghost" size="icon" onClick={() => navigate("/admin")}> 
                        <ArrowLeft className="h-5 w-5" />
                    </Button>
                    <div>
                        <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Admin</div>
                        <h1 className="font-heading font-bold text-3xl">Manajemen User</h1>
                        <p className="text-muted-foreground mt-1">Kelola akun mitra, admin, dan status akses.</p>
                    </div>
                </div>

                <Card className="border-border p-0">
                    <CardHeader>
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                            <div>
                                <CardTitle>Daftar User</CardTitle>
                                <CardDescription>Kelola semua akun terdaftar pada platform.</CardDescription>
                            </div>
                            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                                <Input
                                    aria-label="Cari pengguna"
                                    placeholder="Cari email, nama, nomor telepon"
                                    value={search}
                                    onChange={(e) => { setPage(0); setSearch(e.target.value); }}
                                    className="h-12 rounded-xl max-w-sm"
                                    icon={<Search className="h-4 w-4 text-muted-foreground" />}
                                />
                                <div className="flex items-center gap-2">
                                    <Button variant="outline" size="sm" onClick={refresh} disabled={refreshing} className="rounded-xl">
                                        {refreshing ? "Menyegarkan..." : "Segarkan"}
                                    </Button>
                                    <Button size="sm" onClick={openAddUser} className="rounded-xl bg-brand-green text-white hover:bg-brand-greenDark">
                                        Tambah User
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="p-0">
                        {error && <p role="alert" className="p-4 text-destructive">{error}</p>}
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                                    <tr>
                                        <th className="p-3 text-left font-semibold">Email</th>
                                        <th className="p-3 text-left font-semibold">Nama</th>
                                        <th className="p-3 text-left font-semibold">Role</th>
                                        <th className="p-3 text-left font-semibold">Status</th>
                                        <th className="p-3 text-left font-semibold">ID Pengguna</th>
                                        <th className="p-3 text-left font-semibold">Terdaftar</th>
                                        <th className="p-3 text-center font-semibold">Aksi</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {!users ? (
                                        Array.from({ length: 6 }).map((_, idx) => (
                                            <tr key={idx}><td colSpan={7} className="p-3"><div className="h-8 bg-muted animate-pulse rounded" /></td></tr>
                                        ))
                                    ) : filteredUsers.length === 0 ? (
                                        <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">Tidak ada user ditemukan.</td></tr>
                                    ) : (
                                        filteredUsers.map((user) => (
                                            <tr key={user.id} className="border-t border-border hover:bg-muted/40 transition-colors">
                                                <td className="p-3 font-medium">{user.email}</td>
                                                <td className="p-3">{user.fullName || user.owner_name || "-"}</td>
                                                <td className="p-3 uppercase">{user.role}</td>
                                                <td className="p-3">
                                                    <span className={`inline-flex rounded-full px-2 py-1 text-[10px] font-semibold ${user.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
                                                        {user.status}
                                                    </span>
                                                </td>
                                                <td className="p-3 text-xs break-all select-all">{user.id}</td>
                                                <td className="p-3 text-muted-foreground">{formatDate(user.createdAt || user.created_at)}</td>
                                                <td className="p-3 text-center space-y-2">
                                                    <div className="flex flex-wrap items-center justify-center gap-2">
                                                        <Button size="sm" variant="outline" onClick={() => toggleStatus(user)} disabled={saving}>
                                                            {user.status === "active" ? "Nonaktifkan" : "Aktifkan"}
                                                        </Button>
                                                        <Button size="sm" variant="ghost" className="rounded-xl" onClick={() => editUser(user)} disabled={saving}>
                                                            <Edit3 className="h-4 w-4" /> Edit
                                                        </Button>
                                                        <Button size="sm" variant="destructive" className="rounded-xl" onClick={() => deleteUser(user)} disabled={saving}>
                                                            <Trash2 className="h-4 w-4" /> Hapus
                                                        </Button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                        <div className="p-4 flex flex-wrap justify-between gap-3 items-center text-sm"><span>{total} pengguna · Halaman {page + 1}</span><div className="flex gap-2"><Button variant="outline" disabled={!users || page === 0} onClick={() => setPage(page - 1)}>Sebelumnya</Button><Button variant="outline" disabled={!users || (page + 1) * 20 >= total} onClick={() => setPage(page + 1)}>Berikutnya</Button></div></div>
                    </CardContent>
                </Card>
                {/* Dialog: Add / Edit User */}
                <Dialog open={modalOpen} onOpenChange={(open) => { if (!saving) setModalOpen(open); }}>
                    <DialogContent className="max-h-[90dvh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle>{modalMode === "add" ? "Tambah User" : "Edit User"}</DialogTitle>
                            <DialogDescription>
                                {modalMode === "add" ? "Buat akun baru (mitra atau admin)." : "Perbarui data user."}
                            </DialogDescription>
                        </DialogHeader>

                        <div className="grid gap-3 py-2">
                            <div>
                                <Label>Email</Label>
                                <Input aria-label="Email" disabled={modalMode === "edit"} maxLength={180} value={form.email} onChange={(e) => setForm((s) => ({ ...s, email: e.target.value }))} placeholder="email@contoh.com" type="email" className="mt-1" />
                            </div>
                            <div>
                                <Label>Nama</Label>
                                <Input aria-label="Nama" maxLength={120} value={form.fullName} onChange={(e) => setForm((s) => ({ ...s, fullName: e.target.value }))} placeholder="Nama lengkap" className="mt-1" />
                            </div>
                            <div>
                                <Label>Role</Label>
                                <select aria-label="Role" value={form.role} onChange={(e) => setForm((s) => ({ ...s, role: e.target.value }))} className="w-full mt-1 h-10 rounded-xl border border-border bg-background px-3">
                                    <option value="mitra">Mitra</option>
                                    <option value="admin">Admin</option>
                                    <option value="super_admin">Super Admin</option>
                                    <option value="customer">Customer</option>
                                    <option value="developer">Developer</option>
                                </select>
                            </div>
                            {form.role === "mitra" && <div><Label>Nama warung {modalMode === "edit" && "(opsional)"}</Label><Input aria-label="Nama warung" maxLength={120} value={form.shopName} onChange={(e) => setForm((s) => ({ ...s, shopName: e.target.value }))} placeholder={modalMode === "edit" ? "Kosongkan untuk mempertahankan nama" : "Nama warung"} className="mt-1" /></div>}
                            <div>
                                <Label>{modalMode === "add" ? "Password (sementara)" : "Ubah Password (opsional)"}</Label>
                                <Input aria-label="Password" type="password" autoComplete="new-password" maxLength={128} value={form.password} onChange={(e) => setForm((s) => ({ ...s, password: e.target.value }))} placeholder={modalMode === "add" ? "Min. 8 karakter, huruf dan angka" : "Kosongkan jika tidak ingin mengubah"} className="mt-1" />
                            </div>
                        </div>

                        <DialogFooter>
                            <Button variant="ghost" disabled={saving} onClick={() => setModalOpen(false)}>Batal</Button>
                            <Button onClick={saveUser} disabled={saving} className="ml-2">{saving ? "Menyimpan..." : modalMode === "add" ? "Tambah" : "Simpan"}</Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </div>
    );
}

